/**
 * Services transport boundary.
 *
 * Customers never write operational requests straight into Postgres. The
 * Edge Function owns identity, rate limits, national-location verification,
 * idempotency and the state transition. Preview takes an explicit fixture-only
 * path before any Supabase call, so a preview APK can never create a real
 * dispatch request.
 */
import { ServiceError } from '../errors/service-error'
import { isIsolatedPreviewBuild } from '../runtime-build'
import { supabase } from '../supabase'
import { serviceRequestInputSchema } from '../validations/services.schema'
import {
  canRequesterCancelServiceRequest,
  canRequesterAcceptServiceQuote,
  isServiceCategory,
  isServiceRequestStatus,
  isServiceTiming,
  isServiceWindow,
  serviceNeedBelongsToCategory,
  serviceNeedRequiresDestination,
  type ServiceLocation,
  type ServiceQuote,
  type ServiceRequest,
  type ServiceRequestEvent,
  type ServiceRequestInput,
} from './services-catalog'
import {
  previewAcceptServiceQuote,
  previewCancelServiceRequest,
  previewCreateServiceRequest,
  previewGetServiceRequest,
  previewListServiceRequests,
} from '../../preview/services'

type UnknownRecord = Record<string, unknown>

function asRecord(value: unknown): UnknownRecord | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as UnknownRecord)
    : null
}

function invalidResponse(): ServiceError {
  return new ServiceError({
    message: 'Services returned an invalid response',
    code: 'SERVICES_RESPONSE_INVALID',
    statusCode: 502,
  })
}

function asText(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value : null
}

function isIsoDate(value: unknown): value is string {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false
  const parsed = new Date(`${value}T12:00:00.000Z`)
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value
}

function mapLocation(value: unknown): ServiceLocation {
  const raw = asRecord(value)
  const label = asText(raw?.label)
  const latitude = raw?.latitude
  const longitude = raw?.longitude
  if (
    !label ||
    typeof latitude !== 'number' ||
    !Number.isFinite(latitude) ||
    typeof longitude !== 'number' ||
    !Number.isFinite(longitude)
  ) {
    throw invalidResponse()
  }
  return { label, latitude, longitude }
}

function mapNullableLocation(value: unknown): ServiceLocation | null {
  if (value === null || value === undefined) return null
  return mapLocation(value)
}

function mapEvent(value: unknown): ServiceRequestEvent {
  const raw = asRecord(value)
  const id = asText(raw?.id)
  const kind = raw?.kind
  const createdAt = asText(raw?.createdAt ?? raw?.created_at)
  if (!id || !createdAt || (kind !== 'created' && !isServiceRequestStatus(kind))) {
    throw invalidResponse()
  }
  return { id, kind, createdAt }
}

function mapQuote(value: unknown): ServiceQuote | null {
  if (value === null || value === undefined) return null
  const raw = asRecord(value)
  const amountCents = raw?.amountCents ?? raw?.amount_cents
  const currency = raw?.currency
  const summary = asText(raw?.summary)
  const createdAt = asText(raw?.createdAt ?? raw?.created_at)
  const acceptedAt = raw?.acceptedAt ?? raw?.accepted_at ?? null
  if (
    !Number.isInteger(amountCents) ||
    typeof amountCents !== 'number' ||
    amountCents <= 0 ||
    amountCents > 50_000_000 ||
    currency !== 'USD' ||
    !summary ||
    summary.length < 12 ||
    summary.length > 1200 ||
    !createdAt ||
    !(acceptedAt === null || typeof acceptedAt === 'string')
  ) {
    throw invalidResponse()
  }
  return { amountCents, currency, summary, createdAt, acceptedAt }
}

function mapRequest(value: unknown): ServiceRequest {
  const raw = asRecord(value)
  const id = asText(raw?.id)
  const category = raw?.category
  const need = asText(raw?.need)
  const timing = raw?.timing
  const scheduledDate = raw?.scheduledDate ?? raw?.scheduled_date ?? null
  const arrivalWindow = raw?.arrivalWindow ?? raw?.arrival_window
  const details = asText(raw?.details)
  const status = raw?.status
  const createdAt = asText(raw?.createdAt ?? raw?.created_at)
  const updatedAt = asText(raw?.updatedAt ?? raw?.updated_at)
  const rawEvents = raw?.events
  let location: ServiceLocation
  let destination: ServiceLocation | null
  try {
    location = mapLocation(raw?.location ?? raw?.service_location)
    destination = mapNullableLocation(raw?.destination ?? raw?.destination_location)
  } catch {
    throw invalidResponse()
  }
  let quote: ServiceQuote | null
  try {
    quote = mapQuote(raw?.quote)
  } catch {
    throw invalidResponse()
  }
  const needsDestination =
    isServiceCategory(category) && need !== null
      ? serviceNeedRequiresDestination(category, need)
      : false
  if (
    !id ||
    !isServiceCategory(category) ||
    !need ||
    !serviceNeedBelongsToCategory(category, need) ||
    !isServiceTiming(timing) ||
    !(scheduledDate === null || isIsoDate(scheduledDate)) ||
    (timing === 'scheduled' && scheduledDate === null) ||
    (timing === 'now' && scheduledDate !== null) ||
    !isServiceWindow(arrivalWindow) ||
    !details ||
    !isServiceRequestStatus(status) ||
    !createdAt ||
    !updatedAt ||
    !Array.isArray(rawEvents) ||
    rawEvents.length === 0 ||
    (needsDestination && destination === null) ||
    (!needsDestination && destination !== null) ||
    (status === 'quoted' && !quote) ||
    (['confirmed', 'in_progress', 'completed'].includes(status) && quote?.acceptedAt === null)
  ) {
    throw invalidResponse()
  }
  return {
    id,
    category,
    need,
    location,
    destination,
    timing,
    scheduledDate,
    arrivalWindow,
    details,
    status,
    createdAt,
    updatedAt,
    canCancel: canRequesterCancelServiceRequest(status),
    canAcceptQuote: canRequesterAcceptServiceQuote(status, quote),
    quote,
    events: rawEvents.map(mapEvent),
  }
}

function edgeFailure(error: unknown): ServiceError {
  const item = error as {
    message?: string
    status?: number
    statusCode?: number
    context?: { code?: string; error?: string } | null
  }
  const body = item.context
  return new ServiceError({
    message: body?.error ?? item.message ?? 'Services request failed',
    code: body?.code ?? 'SERVICES_REQUEST_FAILED',
    statusCode: item.statusCode ?? item.status ?? 500,
  })
}

function validatedInput(input: ServiceRequestInput): ServiceRequestInput {
  const parsed = serviceRequestInputSchema.safeParse(input)
  if (parsed.success) return parsed.data
  throw new ServiceError({
    message: 'Service request is incomplete or invalid',
    code: 'SERVICES_VALIDATION_ERROR',
    statusCode: 422,
    details: parsed.error.flatten(),
  })
}

class ServicesService {
  async listRequests(): Promise<ServiceRequest[]> {
    if (isIsolatedPreviewBuild) return previewListServiceRequests()
    const { data, error } = await supabase.functions.invoke<{ requests?: unknown }>(
      'services-request',
      {
        body: { action: 'list' },
      },
    )
    if (error) throw edgeFailure(error)
    if (!Array.isArray(data?.requests)) throw invalidResponse()
    return data.requests.map(mapRequest)
  }

  async getRequest(id: string): Promise<ServiceRequest | null> {
    if (!id.trim()) return null
    if (isIsolatedPreviewBuild) return previewGetServiceRequest(id)
    const { data, error } = await supabase.functions.invoke<{ request?: unknown }>(
      'services-request',
      {
        body: { action: 'get', requestId: id },
      },
    )
    if (error) {
      const normalized = edgeFailure(error)
      if (normalized.statusCode === 404 || normalized.code === 'SERVICES_NOT_FOUND') return null
      throw normalized
    }
    return data?.request ? mapRequest(data.request) : null
  }

  async createRequest(input: ServiceRequestInput): Promise<ServiceRequest> {
    const request = validatedInput(input)
    if (isIsolatedPreviewBuild) return previewCreateServiceRequest(request)
    const { data, error } = await supabase.functions.invoke<{ request?: unknown }>(
      'services-request',
      {
        body: { action: 'create', request },
      },
    )
    if (error) throw edgeFailure(error)
    if (!data?.request) throw invalidResponse()
    return mapRequest(data.request)
  }

  async cancelRequest(id: string): Promise<ServiceRequest> {
    if (!id.trim()) {
      throw new ServiceError({
        message: 'A service request id is required',
        code: 'SERVICES_VALIDATION_ERROR',
        statusCode: 422,
      })
    }
    if (isIsolatedPreviewBuild) return previewCancelServiceRequest(id)
    const { data, error } = await supabase.functions.invoke<{ request?: unknown }>(
      'services-request',
      {
        body: { action: 'cancel', requestId: id },
      },
    )
    if (error) throw edgeFailure(error)
    if (!data?.request) throw invalidResponse()
    return mapRequest(data.request)
  }

  async acceptQuote(id: string): Promise<ServiceRequest> {
    if (!id.trim()) {
      throw new ServiceError({
        message: 'A service request id is required',
        code: 'SERVICES_VALIDATION_ERROR',
        statusCode: 422,
      })
    }
    if (isIsolatedPreviewBuild) return previewAcceptServiceQuote(id)
    const { data, error } = await supabase.functions.invoke<{ request?: unknown }>(
      'services-request',
      {
        body: { action: 'accept_quote', requestId: id },
      },
    )
    if (error) throw edgeFailure(error)
    if (!data?.request) throw invalidResponse()
    return mapRequest(data.request)
  }
}

export const servicesService = new ServicesService()
